import { useCallback, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Col, Row, Tab, Tabs } from 'react-bootstrap';
import BackButton from 'components/global/BackButton';
import edoUsersSignal from 'signals/EdoUsers.signal';
import {
  setEdoSettingsUserDetailUser,
  handleProgramAccessUpdate,
  handleLenderAccessUpdate,
  handleSelectAllLenders,
  fetchAndSetUsersData,
} from '../EdoUsers.helpers';
import UserDetailsTab from './UserDetailsTab';
import ProgramAccess from 'components/global/ProgramAccess/ProgramAccess';
import LenderAccess from 'components/global/LenderAccess/LenderAccess';
import loaderSignal from 'signals/Loader.signal';
import userAccountSignal from 'signals/UserAccount.signal';

const EdoUserDetailsView = () => {
  const { userId } = useParams();
  const history = useHistory();
  const { selectedUser, form, fundingPrograms, lenders } = edoUsersSignal.value;
  const { userData } = userAccountSignal.value;
  
  // Only Admin level users can configure Program Access and Lender Access
  const canConfigureAccess = ['OWNER', 'ADMIN', 'SUPER_ADMIN'].includes(userData?.role);

  const loadUserData = useCallback(async () => {
    if (!userId) return;
    
    loaderSignal.update({ isContentLoading: true });
    try {
      await fetchAndSetUsersData(true);
      const user = edoUsersSignal.value.users.find(
        (u) => String(u.id) === String(userId)
      );
      if (user) {
        setEdoSettingsUserDetailUser(user);
      } else {
        // User not found, redirect back to users list
        history.push('/edo/users');
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      history.push('/edo/users');
    } finally {
      loaderSignal.reset();
    }
  }, [userId, history]);

  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  if (!selectedUser || String(selectedUser.id) !== String(userId)) {
    return (
      <div className="bg-white p-32 border border-gray shadow-sm">
        <div className="text-center">
          <p>Loading user details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-32 border border-gray shadow-sm">
      <Row className="mb-16">
        <Col>
          <BackButton
            onClick={() => history.push('/edo/users')}
            buttonText="Back to Users"
          />
        </Col>
      </Row>
      <Row className="mb-16">
        <Col>
          <h4>
            {selectedUser.firstName} {selectedUser.lastName}
          </h4>
          <p className="text-muted mb-0">{selectedUser.email}</p>
        </Col>
      </Row>
      <Tabs className="mb-8" defaultActiveKey="userDetails">
        <Tab eventKey="userDetails" title="User Details">
          <UserDetailsTab />
        </Tab>
        {canConfigureAccess && (
          <Tab eventKey="programAccess" title="Program Access">
            <ProgramAccess
              programAccesses={selectedUser?.edoUserProgramAccesses}
              fundingPrograms={fundingPrograms}
              userRolesPerProgram={form.edoUserProgramAccesses}
              onProgramAccessUpdate={handleProgramAccessUpdate}
            />
          </Tab>
        )}
        {canConfigureAccess && (
          <Tab eventKey="lenderAccess" title="Lender Access">
            <LenderAccess
              lenderAccesses={selectedUser?.edoUserLenderAccesses}
              selectedLenderIds={form.edoUserLenderAccesses || []}
              lenders={lenders || []}
              onLenderAccessUpdate={handleLenderAccessUpdate}
              onSelectAllLenders={handleSelectAllLenders}
            />
          </Tab>
        )}
      </Tabs>
    </div>
  );
};

export default EdoUserDetailsView;

